import React, { useCallback, useState, useRef, useEffect } from 'react'
import Modal from 'react-modal'
import Textarea from 'react-textarea-autosize'
import classNames from 'classnames'
import { useSelector, useDispatch } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import _ from 'lodash'

import api from 'Src/api'

import { getBotById } from 'Selectors/bots'
import { canModifyDeveloperSettings } from 'Selectors/users'

import { getAccountFeatures } from 'Selectors/account'
import CodeMirrorComponent from 'Src/components/Codemirror'
import { Tabs, Tab } from 'Src/components/Tabs'
import ToggleSwitch from 'Src/components/Toggle'

import { fetchBots } from 'Actions/bots'

import WidgetInstallScript from './WidgetInstallScript'

import exclamation from '../../assets/images/exclamation.svg?url'

const PATCH_BOTS_URL = 'v1/bot-profiles/:id'
const RESEND_VERIFICATION_EMAIL_URL = 'v1/bot-profiles/:bot_id/transcript-email/resend'

const updateBot = async (botId, data = {}) => {
  return await api.patch(PATCH_BOTS_URL, data, { urlParams: { id: botId } })
}

const resendVerificationEmail = async (botId) => {
  try {
    await api.post(RESEND_VERIFICATION_EMAIL_URL, {}, { urlParams: { bot_id: botId } })
    alert('Verification Email Sent')
  } catch (error) {
    if (error?.response?.data?.message) {
      alert(error.response.data.message)
    } else {
      alert('Verification Email Sent Failed')
    }
  }
}

const widgetTypeDropdownOptions = [
  { value: 'HappyFox Chat Widget', label: 'HappyFox Chat Widget' },
  { value: 'Bot Widget', label: 'Bot Widget' }
]

function getTranscriptEmail(transcriptEmailSettings) {
  let email
  if (transcriptEmailSettings) {
    email = transcriptEmailSettings.email
    if (transcriptEmailSettings.email_change_request?.email) {
      email = transcriptEmailSettings.email_change_request?.email
    }
  }
  return email
}

const EditBotModal = ({ isOpen, hide, botId }) => {
  const dispatch = useDispatch()
  const textAreaRef = useRef()

  const bot = useSelector((state) => getBotById(state, botId))
  const accountFeatures = useSelector(getAccountFeatures)

  const isBotWidgetAccount = accountFeatures?.bot_widget ?? true

  const botSettings = JSON.stringify(_.pick(bot, ['settings']), null, 2)

  const [settings, setSettings] = useState(botSettings)
  const [copiedButtonText, setCopiedButtonText] = useState('Copy to Clipboard')

  const canModifySettings = useSelector(canModifyDeveloperSettings)

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    control
  } = useForm({
    defaultValues: {
      name: bot.name,
      displayName: bot.display_name,
      widget_type: '',
      status: bot.status === 'active',
      transcriptEmail: getTranscriptEmail(bot.settings?.transcript_email)
    }
  })

  const onClose = useCallback(hide, [hide])

  useEffect(() => {
    const value = widgetTypeDropdownOptions.find((option) => option.value === bot.widget_type)

    return setValue('widget_type', value)
  }, [bot, setValue])

  const onSubmit = async (formData) => {
    const { name, displayName, widget_type, status, transcriptEmail } = formData

    let data = {}
    try {
      data = JSON.parse(settings)
    } catch (error) {
      console.error('Unable to parse json', error)
      alert('Invalid JSON')
      return
    }

    const payload = {
      name,
      displayName,
      widget_type: widget_type.value,
      settings: data?.settings ?? {},
      status: status ? 'active' : 'inactive',
      transcriptEmail
    }

    try {
      await updateBot(bot.id, payload)
      dispatch(fetchBots())
      alert('Bot settings updated successfully')
    } catch (err) {
      console.error('Error while updating bot settings', err)
      alert('Failed to update settings')
    }
  }

  const onChangeSettings = useCallback((value) => {
    setSettings(value)
  }, [])

  const content = WidgetInstallScript({ EMBED_TOKEN: bot.embed_token })

  const copyToClipboard = () => {
    textAreaRef.current.select()
    document.execCommand('copy')
    setCopiedButtonText('Copied')

    setTimeout(() => {
      setCopiedButtonText('Copy to Clipboard')
      textAreaRef.current.setSelectionRange(0, 0)
      textAreaRef.current.blur()
    }, 3000)
  }

  const isEmailChangeRequestExists = bot.settings?.transcript_email?.email_change_request?.email

  return (
    <div>
      <Modal isOpen={isOpen} contentLabel='Update Bot Modal' ariaHideApp={false} onRequestClose={onClose}>
        <div className='modal-header'>
          <h2>Bot Settings</h2>
          <div className='modal-close' onClick={onClose}>
            &times;
          </div>
        </div>
        <div className='modal-body'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='form-group'>
              <label>Bot Name</label>
              <input
                className={classNames('form-control', { 'is-invalid': errors.name })}
                name='name'
                {...register('name', {
                  required: 'Name is required',
                  minLength: {
                    value: 3,
                    message: 'Name should contain at least 3 characters'
                  }
                })}
              />
              {errors.name && <small className='form-text text-danger'>{errors.name.message}</small>}
            </div>
            <div className='form-group'>
              <label>Display Name</label>
              <input
                className={classNames('form-control', { 'is-invalid': errors.displayName })}
                name='displayName'
                {...register('displayName', {
                  required: 'Display name is required',
                  minLength: {
                    value: 3,
                    message: 'Display name should contain at least 3 characters'
                  }
                })}
              />
              {errors.displayName && <small className='form-text text-danger'>{errors.displayName.message}</small>}
            </div>
            <div className='form-group'>
              <label>Bot Transcripts</label>
              <div className='text-small'>
                You can forward all transcripts from your organization to an e-mail address
              </div>
              <input
                name='transcriptEmail'
                className={classNames('form-control', { 'is-invalid': errors.displayName }, 'mb-2', 'mr-3')}
                {...register('transcriptEmail', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
              {isEmailChangeRequestExists && (
                <div className='d-flex'>
                  <div>
                    <img src={exclamation} className='mr-2' />
                  </div>
                  <span className='text-small mr-2'>Verify the above email address to receive transcripts.</span>
                  <span className='text-primary' style={{ cursor: 'pointer' }}>
                    <a
                      onClick={() => {
                        resendVerificationEmail(botId)
                      }}
                    >
                      Resend Verification Email
                    </a>
                  </span>
                </div>
              )}
              {errors.transcriptEmail && (
                <small className='form-text text-danger'>{errors.transcriptEmail.message}</small>
              )}
            </div>
            <div className='form-group d-flex'>
              <label>Enable Bot Widget</label>
              <Controller
                name='status'
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <ToggleSwitch
                      checked={value}
                      className='ml-auto'
                      onClick={() => {
                        onChange(!value)
                      }}
                    />
                  )
                }}
              />
            </div>

            {canModifySettings && (
              <div className='form-group'>
                <label>Widget</label>
                <Controller
                  control={control}
                  name='widget_type'
                  render={({ field }) => {
                    return (
                      <Select
                        options={widgetTypeDropdownOptions}
                        styles={{ menuPortal: (styles) => ({ ...styles, zIndex: 9999999 }) }}
                        menuPortalTarget={document.getElementsByClassName('ReactModalPortal')[0]}
                        menuPlacement='auto'
                        isOptionDisabled={(option) => option.value === 'Bot Widget' && !isBotWidgetAccount}
                        {...field}
                      />
                    )
                  }}
                />
              </div>
            )}
            {/* <ul class="nav nav-tabs">
            <li class="nav-item">
              <a class="nav-link active">Installation Code</a>
            </li>
            <li class="nav-item">
              <a class="nav-link">Advanced</a>
            </li>
          </ul> */}
            <Tabs>
              <Tab hidden={!isBotWidgetAccount} title='Installation Code'>
                <div className='d-flex align-items-center mb-3'>
                  <div className='w-75'>
                    To install the chatbot, paste the below install code before the closing &lt;/body&gt; tag on your
                    website's footer
                  </div>
                  <div className='button-secondary ml-auto' onClick={copyToClipboard}>
                    {copiedButtonText}
                  </div>
                </div>
                <Textarea
                  className='form-control mb-2'
                  name='defaultValue'
                  ref={textAreaRef}
                  defaultValue={content}
                  maxRows={10}
                  minRows={3}
                  readOnly
                  style={{ resize: 'none' }}
                />
              </Tab>
              {canModifySettings && (
                <Tab title='Advanced Settings'>
                  <CodeMirrorComponent value={botSettings} onChange={onChangeSettings} />
                </Tab>
              )}
            </Tabs>

            <button className='button-primary save' type='submit'>
              Save
            </button>
          </form>
        </div>
      </Modal>
    </div>
  )
}

export default EditBotModal
